// Auth Store - Gestión de autenticación con Zustand
import { create } from 'zustand';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signOut,
  onAuthStateChanged,
  type User
} from 'firebase/auth';
import { auth } from '../lib/firebase';
import { usersService, type UserRole } from '../lib/usersService';
// A-01: Imports para limpiar stores dependientes de forma síncrona al hacer logout
import { useWorkflowsStore } from './workflowsStore';
import { useExcelFilesStore } from './excelFilesStore';

// Mapea códigos de error de Firebase Auth a mensajes claros en español
function getFirebaseErrorMessage(error: unknown): string {
  const code = (error as { code?: string })?.code ?? '';
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'Correo electrónico o contraseña incorrectos';
    case 'auth/invalid-email':
      return 'El correo electrónico no tiene un formato válido';
    case 'auth/user-disabled':
      return 'Esta cuenta ha sido deshabilitada. Contacta al administrador.';
    case 'auth/too-many-requests':
      return 'Demasiados intentos fallidos. Espera unos minutos e intenta de nuevo.';
    case 'auth/network-request-failed':
      return 'Error de conexión. Verifica tu internet e intenta de nuevo.';
    case 'auth/email-already-in-use':
      return 'Este correo electrónico ya está registrado. Intenta iniciar sesión.';
    case 'auth/weak-password':
      return 'La contraseña es muy débil. Usa al menos 8 caracteres.';
    case 'auth/operation-not-allowed':
      return 'Este método de inicio de sesión no está habilitado.';
    default:
      return error instanceof Error ? error.message : 'Error inesperado. Intenta de nuevo.';
  }
}

interface AuthStore {
  user: User | null;
  // Rol del usuario: 'admin' | 'user' | null (null = no inicializado)
  userRole: UserRole | null;
  // Estado activo del usuario: false = cuenta desactivada por admin
  userActive: boolean;
  // Estado de verificación de email
  emailVerified: boolean;
  loading: boolean;
  error: string | null;
  initialized: boolean;
  // [H-B011] Flag para indicar que la creacion de perfil fallo.
  // Permite al UI mostrar advertencia si es necesario.
  profileError: string | null;
  // Helper para verificar si el usuario actual es admin
  isAdmin: () => boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  cleanup: () => void;
  // Verificación de email
  resendVerificationEmail: () => Promise<void>;
  checkEmailVerification: () => Promise<boolean>;
}

export const useAuthStore = create<AuthStore>((set, get) => {
  // Inicializar listener de autenticación
  let unsubscribe: (() => void) | null = null;
  // [H-F003] Flag para evitar race condition: signUp crea el perfil,
  // y onAuthStateChanged no debe intentar crearlo simultáneamente.
  let isSigningUp = false;

  const init = () => {
    if (unsubscribe) return;

    set({ loading: true });
    unsubscribe = onAuthStateChanged(
      auth,
      async (user) => {
        // Si el usuario existe pero no tiene perfil, crearlo
        // [H-B010] Reintento con backoff exponencial (max 2 reintentos) para errores transitorios.
        // [H-F003] Si signUp está en curso, no competir por la creación del perfil
        if (user && !isSigningUp) {
          let profileRetries = 0;
          const maxRetries = 2;
          while (profileRetries <= maxRetries) {
            try {
              const profile = await usersService.getUserProfile(user.uid);
              if (!profile) {
                await usersService.createUserProfile(user, { email: user.email || '' });
                set({ userRole: 'user', userActive: true, profileError: null });
              } else {
                // Leer rol y estado activo del perfil
                const role = profile.role || 'user';
                const active = profile.active !== false; // Ausencia = activo

                // [H-F002] Si el usuario fue desactivado, limpiar user ANTES de signOut
                // para evitar que el store tenga user seteado durante el signOut
                if (!active) {
                  set({ user: null, emailVerified: false, userRole: null, userActive: false, profileError: 'Tu cuenta ha sido desactivada. Contacta al administrador.' });
                  await signOut(auth);
                  return;
                }

                set({ userRole: role, userActive: active, profileError: null });
              }
              break; // Exito, salir del loop
            } catch (e) {
              profileRetries++;
              const errorMsg = e instanceof Error ? e.message : 'Error desconocido';
              if (profileRetries > maxRetries) {
                // [H-B010] Almacenar flag para que el UI pueda mostrar advertencia
                console.error('Error obteniendo/creando perfil tras reintentos:', e);
                set({ profileError: `No se pudo sincronizar el perfil: ${errorMsg}` });
              } else {
                // Backoff exponencial: 1s, 2s
                await new Promise(r => setTimeout(r, 1000 * profileRetries));
              }
            }
          }
        }

        set({ user, emailVerified: user?.emailVerified ?? false, loading: false, initialized: true, error: null });
      },
      (error) => {
        set({
          user: null,
          loading: false,
          initialized: true,
          error: error.message
        });
      }
    );
  };

  // Inicializar al crear el store
  init();

  return {
    user: null,
    userRole: null,
    userActive: true,
    emailVerified: false,
    loading: true,
    error: null,
    initialized: false,
    profileError: null,

    isAdmin: (): boolean => get().userRole === 'admin',

    signIn: async (email: string, password: string) => {
      try {
        set({ loading: true, error: null });
        await signInWithEmailAndPassword(auth, email, password);
        set({ loading: false, error: null });
      } catch (error) {
        set({ loading: false, error: getFirebaseErrorMessage(error) });
        throw error;
      }
    },

    signUp: async (email: string, password: string) => {
      try {
        set({ loading: true, error: null });
        // [H-F003] Señalar que signUp está en curso para que onAuthStateChanged no compita
        isSigningUp = true;
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);

        // Obtener el token para forzar la propagacion de autenticacion en Firestore
        try {
          await userCredential.user.getIdToken(true);
        } catch (e) {
          if (import.meta.env.DEV) console.warn('Error obteniendo token:', e);
        }

        // Crear perfil de usuario en Firestore
        // [H-B011] Si falla, onAuthStateChanged lo reintentara como fallback (con backoff).
        try {
          await usersService.createUserProfile(userCredential.user, { email });
          set({ profileError: null });
        } catch (e) {
          const errorMsg = e instanceof Error ? e.message : 'Error desconocido';
          console.error('Error creando perfil en signUp (onAuthStateChanged reintentará):', e);
          set({ profileError: `No se pudo crear el perfil: ${errorMsg}` });
        }

        // Enviar email de verificación al nuevo usuario
        try {
          await sendEmailVerification(userCredential.user);
        } catch (e) {
          if (import.meta.env.DEV) console.warn('Error enviando email de verificación:', e);
          // No bloquear el registro si falla el envío del email
        }

        // [H-F003] Liberar flag para que onAuthStateChanged pueda operar normalmente
        isSigningUp = false;
        set({ loading: false, error: null });
      } catch (error) {
        isSigningUp = false;
        set({ loading: false, error: getFirebaseErrorMessage(error) });
        throw error;
      }
    },

    logout: async () => {
      try {
        set({ loading: true, error: null });
        // A-01: Limpiar stores dependientes ANTES de signOut para evitar
        // ventana de tiempo donde datos del usuario anterior son visibles
        useWorkflowsStore.setState({
          workflows: [],
          currentWorkflow: null,
          importDraft: null,
          loading: false,
          error: null,
        });
        useExcelFilesStore.getState().clearFiles();
        await signOut(auth);
        set({ user: null, userRole: null, userActive: true, emailVerified: false, loading: false, error: null, profileError: null });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Error al cerrar sesión';
        set({ loading: false, error: message });
        throw error;
      }
    },

    clearError: () => {
      set({ error: null });
    },

    // Reenviar email de verificación al usuario actual
    resendVerificationEmail: async () => {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('No hay usuario autenticado');
      if (currentUser.emailVerified) return;
      await sendEmailVerification(currentUser);
    },

    // Verificar si el usuario ya confirmó su email (llama reload() para refrescar)
    checkEmailVerification: async () => {
      const currentUser = auth.currentUser;
      if (!currentUser) return false;
      await currentUser.reload();
      const verified = currentUser.emailVerified;
      set({ emailVerified: verified });
      return verified;
    },

    // A-02: cleanup() desuscribe el listener de onAuthStateChanged.
    // En una SPA, el listener persiste toda la vida de la app (correcto).
    // Solo llamar en tests o si se necesita desmontar el auth system.
    cleanup: () => {
      if (unsubscribe) {
        unsubscribe();
        unsubscribe = null;
      }
    },
  };
});

